import type { SearchBoxRetrieveResponse } from "@mapbox/search-js-core";
import type { NearMeFilterConfiguration } from "@/react/components/FilterKit/types";
import { ModalManager } from "@/react/components/Layout/ModalManagerProvider";
import { useModal } from "@/react/components/Layout/ModalManagerProvider";
import type { LocationData } from "@/react/components/shared/Filter/FilterTypes/MapboxInputFilter";
import { Form } from "@/react/components/shared/uikit/Form";
import { useUpdateProfileInfo } from "@/react/hooks/profile/useProfileInfo";
import { usePunditUserContext } from "@circle-react/contexts";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Body } from "./Body";
import { Footer } from "./Footer";
import { Header } from "./Header";

interface UpdateLocationModalProps {
  onSuccess?: (data: { location: LocationData }) => void;
  payload: NearMeFilterConfiguration["payload"];
}

interface LocationOutput {
  data: SearchBoxRetrieveResponse;
  text: string;
  type: "mapbox_v6";
}

export interface UpdateLocationModalData {
  location: LocationOutput | string;
}

const isLocationOutput = (
  location: UpdateLocationModalData["location"],
): location is LocationOutput =>
  typeof location === "object" && "data" in location;

const UpdateLocationModalComponent = ({
  payload,
  onSuccess,
}: UpdateLocationModalProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const updateProfileInfoMutation = useUpdateProfileInfo({
    publicId: currentCommunityMember?.public_uid ?? "",
  });

  const modal = useModal();

  const hasLocation = Boolean(payload.community_member_profile_field?.text);

  const onSubmit = (data: UpdateLocationModalData) => {
    updateProfileInfoMutation.mutate(
      {
        community_member: {
          community_member_profile_fields_attributes: [
            {
              id: payload.community_member_profile_field?.id,
              payload: data.location,
            },
          ],
        },
        skip_profile_field_validation: true,
      },
      {
        onSuccess: () => {
          isLocationOutput(data.location) &&
            onSuccess?.({
              location: {
                id: data.location.data.features[0].properties.mapbox_id,
                name: data.location.data.features[0].properties.full_address,
                source: data.location.type,
              },
            });
        },
      },
    );
  };
  return (
    <Modal isOpen={modal.visible} onClose={modal.hide}>
      <Form
        onSubmit={onSubmit}
        defaultValues={{
          location: payload.community_member_profile_field?.text,
        }}
        className="h-full"
        formClassName="h-full"
      >
        <Modal.Overlay />
        <Modal.Content className="!overflow-visible">
          <Header hasLocation={hasLocation} onClose={modal.hide} />
          <Body hasLocation={hasLocation} />
          <Footer hasLocation={hasLocation} onCancel={modal.hide} />
        </Modal.Content>
      </Form>
    </Modal>
  );
};

const UpdateLocationModal = ModalManager.create(
  (props: UpdateLocationModalProps) => (
    <UpdateLocationModalComponent {...props} />
  ),
);

export const useUpdateLocationModal = () => useModal(UpdateLocationModal);
